import { Row, Tooltip, Text, Icon, Column, StatProps, StatColumn, useAppPalette } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";
import { Divider } from "@mui/material";
import { formatLeverage, LoopPositionExpanded, useLoanMultipliersUtil } from "@bridgesplit/abf-react";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { TooltipContent } from "../..";
import { BaseApy } from "../BaseApy";
import { GenericRewardsApyProps } from "../types";
import { RewardsDailyPointsBreakdown, RewardsDailyCustomPoints } from "../daily-rewards";
import { useRewardsColor } from "../util";
import { RewardsExternalPointsRewards } from "../external-point-rewards/RewardsExternalPointsReward";
import { CaptionWithIcon, RateDisplay } from "../common";

export function LoopPortfolioApy(props: LoopPositionApyTooltipProps) {
    const netApy = props.loopPosition?.userLoopInfo.netApyLive;

    const { loanMultipliers, isLoading } = useLoanMultipliersUtil();
    const color = useRewardsColor(loanMultipliers, props.metadata?.mint, [props.loopscalePointsAction]);

    return (
        <Tooltip title={<LoopPositionApyTooltip {...props} />} reverseColors>
            <Row spacing={0.5}>
                <Row spacing={0.5}>
                    <RateDisplay apy={netApy} variant="body1" color="body" isFeatured={true} />
                    <Text variant="body1" color="caption">
                        APY
                    </Text>
                </Row>
                <Text sx={{ color }} loading={isLoading}>
                    <Icon type="points" />
                </Text>
            </Row>
        </Tooltip>
    );
}

interface LoopPositionApyTooltipProps extends GenericRewardsApyProps {
    customDailyPoints: number | undefined;
    loopPosition: LoopPositionExpanded | undefined;
}

function LoopPositionApyTooltip(props: LoopPositionApyTooltipProps) {
    const borrowApy = props.loopPosition?.loanExpanded.borrowerWAvgApy;
    const baseApy = props.loopPosition?.loopExpanded.loopVault.collateralApyPct;
    const leverage = props.loopPosition?.userLoopInfo.leverageMultiplier ?? 0;

    return (
        <TooltipContent>
            <Column spacing={1}>
                <BaseApy {...props} caption={leverage ? `Net APY` : undefined} variant="body1" />
                <Column sx={{ paddingLeft: 2 }}>
                    <LoopApyBreakdown
                        collateralMetadata={props.loopPosition?.loopExpanded.collateralToken}
                        baseApy={baseApy}
                        borrowApy={borrowApy}
                        leverage={leverage}
                    />
                </Column>
            </Column>
            <Divider />
            <Column spacing={1}>
                <RewardsDailyCustomPoints
                    loopscalePointsAction={props.loopscalePointsAction}
                    metadata={props.metadata}
                    customDailyPoints={props.customDailyPoints}
                />
                <Column sx={{ paddingLeft: 2 }}>
                    <RewardsDailyPointsBreakdown {...props} />
                </Column>
            </Column>
            {props.externalPointRewards?.length && (
                <>
                    <Divider />
                    <RewardsExternalPointsRewards {...props} />
                </>
            )}
        </TooltipContent>
    );
}

function LoopApyBreakdown({
    collateralMetadata,
    baseApy,
    borrowApy,
    leverage
}: {
    collateralMetadata: TokenListMetadata | undefined;
    baseApy: number | undefined;
    borrowApy: number | undefined;
    leverage: number;
}) {
    const { textDisabled } = useAppPalette();
    const ICON_SIZE = 15;

    const stats: StatProps[] = [
        {
            caption: (
                <CaptionWithIcon
                    variant="body1"
                    icon={<Icon type="trending-up" sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                    iconColor={textDisabled}
                    caption={`Base ${collateralMetadata?.symbol} APY`}
                />
            ),
            value: formatPercent(baseApy)
        },

        {
            caption: (
                <CaptionWithIcon
                    icon={<Icon type="currency-exchange" sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                    iconColor={textDisabled}
                    caption={`Borrow APY`}
                    variant="body1"
                />
            ),
            value: formatPercent(borrowApy)
        },
        {
            caption: (
                <CaptionWithIcon
                    icon={<Icon type="leverage" sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                    iconColor={textDisabled}
                    caption={`Leverage`}
                />
            ),
            value: formatLeverage(leverage)
        }
    ];

    return <StatColumn stats={stats} loading={false} spacing={1} />;
}
